import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Text } from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { ColumnOrderState, VisibilityState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SortableColumnRow } from './sortable-column-row.controller';

export interface ColumnOptionsModel {
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
}

interface ListOptionsColumnsStepProps extends ColumnOptionsModel {
  columnLabelMap: Record<string, string>;
  onChange: (columnOptions: ColumnOptionsModel) => void;
}

const KEYBOARD_SENSOR_OPTIONS = {
  coordinateGetter: sortableKeyboardCoordinates,
};
const DND_MODIFIERS = [restrictToVerticalAxis, restrictToParentElement];

const DIACRITICS_REGEX = /\p{Diacritic}/gu;

const normalizeForSearch = (value: string): string =>
  value.normalize('NFD').replace(DIACRITICS_REGEX, '').toLowerCase();

/**
 * Re-applies a reorder computed on a filtered subsequence back onto the full
 * order: columns outside the filtered subset keep their original slot,
 * columns inside it take on the new relative order from `reorderedFiltered`.
 */
const mergeReorderedSubsequence = (
  fullOrder: string[],
  filteredOrder: string[],
  reorderedFiltered: string[],
): string[] => {
  const filteredIds = new Set(filteredOrder);
  let cursor = 0;
  return fullOrder.map((columnId) => {
    if (!filteredIds.has(columnId)) return columnId;
    const replacement = reorderedFiltered[cursor] ?? columnId;
    cursor += 1;
    return replacement;
  });
};

export const ListOptionsColumnsStep = ({
  columnOrder,
  columnVisibility,
  columnLabelMap,
  onChange,
}: ListOptionsColumnsStepProps): React.JSX.Element => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, KEYBOARD_SENSOR_OPTIONS),
  );

  const columnLabels = useMemo(
    () =>
      new Map(
        Object.keys(columnLabelMap).map((columnId) => {
          const labelKey = columnLabelMap[columnId];
          return [columnId, labelKey ? t(labelKey) : columnId];
        }),
      ),
    [columnLabelMap, t],
  );

  const filteredColumnOrder = useMemo(() => {
    const normalizedSearch = normalizeForSearch(search.trim());
    if (!normalizedSearch) return columnOrder;
    return columnOrder.filter((columnId) =>
      normalizeForSearch(columnLabels.get(columnId) ?? columnId).includes(
        normalizedSearch,
      ),
    );
  }, [columnOrder, columnLabels, search]);

  const handleVisibilityChange = (columnId: string, visible: boolean) => {
    onChange({
      columnOrder,
      columnVisibility: { ...columnVisibility, [columnId]: visible },
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = filteredColumnOrder.indexOf(active.id as string);
    const newIndex = filteredColumnOrder.indexOf(over.id as string);
    const reorderedFiltered = arrayMove(
      filteredColumnOrder,
      oldIndex,
      newIndex,
    );

    onChange({
      columnOrder: mergeReorderedSubsequence(
        columnOrder,
        filteredColumnOrder,
        reorderedFiltered,
      ),
      columnVisibility,
    });
  };

  return (
    <Stack direction="column" flex={1} alignItems="stretch" gap={GapEnum.S}>
      <TextInput
        leftIcon="search-md"
        placeholder={t('common.filter.search')}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={DND_MODIFIERS}
        onDragEnd={handleDragEnd}>
        <SortableContext
          items={filteredColumnOrder}
          strategy={verticalListSortingStrategy}>
          <Stack
            direction="column"
            flex={1}
            alignItems="stretch"
            gap={GapEnum.XS}>
            {filteredColumnOrder.length === 0 && (
              <Text>{t('global.no-result-found')}</Text>
            )}
            {filteredColumnOrder.map((columnId, index) => (
              <SortableColumnRow
                key={columnId}
                columnId={columnId}
                label={columnLabels.get(columnId) ?? columnId}
                isVisible={columnVisibility[columnId] !== false}
                isLast={index === filteredColumnOrder.length - 1}
                onVisibilityChange={(visible) =>
                  handleVisibilityChange(columnId, visible)
                }
              />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>
    </Stack>
  );
};
